import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

import { valueTones } from '@/components/ui/key-value-variants'
import type { KeyValueRow } from '@/components/ui/KeyValueList'
import { motionDuration } from '@/lib/motion/motion-duration'
import { cn } from '@/lib/utils'

/** Air between the mark and the panel, and between the panel and the viewport edge. */
const GAP = 8
const EDGE = 8

/** The band card's clearances (mock `pi-tip`): 16 from the viewport, 20 inside its column. */
const BAND_EDGE = 16
const BAND_INSET = 20

/**
 * How long a mouse that leaves the mark has to reach the panel before it closes: long enough to
 * cross the 8px gap, short enough that sweeping across a chart does not leave a trail of panels.
 */
const CLOSE_DELAY_MS = 150

interface Placement {
  top: number
  left: number
  /** The side of the mark the panel landed on, and slides in from. */
  side: TooltipSide
}

/**
 * `left` is for a mark at the viewport's right edge whose neighbours sit above and below it — the
 * chart page's icon rail — where a panel under the mark would cover the next one.
 */
type TooltipSide = 'below' | 'above' | 'left'

/**
 * The panel's skin. `popover` is the chart and heatmap popover (the default). `term` is a glossary
 * term's card (TermTip, the mock's `.term-tip`): the raised fill, 240 at most, the title in the
 * primary ink over a body in the secondary. `chip` is the mock's one-line `data-tip`: a 12/600
 * title on the tooltip fill, no drop shadow.
 *
 * The mock pads `term` 8/10 and `chip` 6/10; both snap to 8 on the 4-grid (nearest-8 tie-break).
 *
 * `card` is the Broker Analysis row card (`RowCard`, the mock's `brb-tip`, br:kit): the popover's
 * skin with its label/value rows 16 apart rather than 24, as the mock spaces them.
 *
 * `band` (sz:kit, owner ruling A3) is a table head's term card (TermTip `variant="band"`, the
 * mock's `pi-tip`): `term`'s raised fill and hairline at the mock's 300 wide (never past the
 * viewport less 16 a side), padded 12/16, a 14/600 title over a 14 body, a 0 8 24 drop. It hangs
 * from the head band's foot rather than the label (see `place`).
 */
export type TooltipVariant = 'popover' | 'term' | 'chip' | 'card' | 'band'

const PANEL_SKIN: Record<TooltipVariant, string> = {
  popover:
    'max-w-70 bg-tooltip px-3 py-2 text-tooltip-foreground shadow-(--tooltip-shadow) ring-1 ring-tooltip-border',
  term: 'max-w-60 bg-popover p-2 text-popover-foreground shadow-(--term-tip-shadow) ring-1 ring-tooltip-border',
  chip: 'max-w-60 bg-tooltip p-2 text-tooltip-foreground ring-1 ring-tooltip-border',
  card: 'max-w-70 bg-tooltip px-3 py-2 text-tooltip-foreground shadow-(--tooltip-shadow) ring-1 ring-tooltip-border',
  band: 'w-[min(--spacing(75),calc(100vw-(--spacing(8))))] bg-popover px-4 py-3 text-wrap text-popover-foreground shadow-(--term-band-shadow) ring-1 ring-tooltip-border',
}

/** The body line under the title: the popover's quieter tertiary, the term card's secondary. */
const BODY_INK: Record<TooltipVariant, string> = {
  popover: 'text-muted-foreground-subtle',
  term: 'text-muted-foreground',
  chip: 'text-muted-foreground',
  card: 'text-muted-foreground-subtle',
  band: 'text-muted-foreground',
}

/** `band` sets the card at 14 (the mock's `t14`); every other skin at 12. */
const TITLE_SIZE: Partial<Record<TooltipVariant, string>> = { band: 'text-h-s' }
const BODY_SIZE: Partial<Record<TooltipVariant, string>> = { band: 'text-body-m' }

interface TooltipPanelProps {
  /** The one line that names what is hovered — "Nabil Securities in Hydropower". */
  title: ReactNode
  /** A sentence of explanation under the title: an ⓘ button's "what is this measure". */
  body?: ReactNode
  /** The facts under it. Empty is allowed: some marks only need the title. */
  rows?: readonly KeyValueRow[] | undefined
  /**
   * A line under the facts, in the link colour, saying what pressing the mark
   * does: the heatmap's "Click to open its names". Never a fact of its own.
   */
  hint?: ReactNode
  /** A wrapped caveat under the facts, in the tertiary ink (the row card's note). */
  note?: ReactNode
  /** Markup between the rows and the note, for a fact the rows cannot hold (the Last minute dots). */
  extra?: ReactNode
  /** `s` is the 14/600 the reference heatmap's panel uses; every other popover stays 12/600. */
  titleSize?: 'xs' | 's' | undefined
  /** See `TooltipVariant`. Default `popover`. */
  variant?: TooltipVariant | undefined
  className?: string | undefined
}

/**
 * The panel on its own, with no trigger and no positioning.
 *
 * Exported because recharts owns the placement of its own tooltips: DonutChart
 * and FundTrendsChart pass this straight to `<Tooltip content={...} />` and the
 * library anchors it. A chart that draws its own marks uses `ChartPopover`
 * (components/charts), and everything else that is not a recharts mark — a
 * sidebar label, an info button, a holdings tile — uses the default export
 * below, which supplies the anchoring this one deliberately lacks.
 *
 * `role="tooltip"` lives here rather than on a wrapper so all three paths
 * announce identically.
 *
 * The reference's chart popover (`brb-tip`, `mkc-hm-tip`): the tooltip fill
 * with a hairline ring and a lift, a 12/600 title, 12px labels a step quieter
 * than body copy and 12px values. It fades in over the fast step as it mounts,
 * which is what a recharts slot gets; `Tooltip` runs its own entrance instead
 * and switches this one off with `fade={false}`.
 */
export function TooltipPanel({
  title,
  body,
  rows = [],
  hint,
  note,
  extra,
  titleSize = 'xs',
  variant = 'popover',
  fade = true,
  className,
}: TooltipPanelProps & { fade?: boolean | undefined }) {
  return (
    <div
      role="tooltip"
      className={cn(
        'w-max',
        PANEL_SKIN[variant],
        fade && 'transition-opacity duration-fast ease-out starting:opacity-0',
        className,
      )}
    >
      <p className={TITLE_SIZE[variant] ?? (titleSize === 's' ? 'text-h-s' : 'text-h-xs')}>
        {title}
      </p>
      {body === undefined ? null : (
        <p className={cn('mt-1', BODY_SIZE[variant] ?? 'text-body-xs', BODY_INK[variant])}>
          {body}
        </p>
      )}
      {rows.length > 0 ? (
        <dl
          className={cn(
            'mt-1 grid grid-cols-[auto_1fr] items-baseline gap-y-1',
            variant === 'card' ? 'gap-x-4' : 'gap-x-6',
          )}
        >
          {rows.map((row) => (
            <Fragment key={row.label}>
              <dt className="text-body-xs text-muted-foreground-subtle">{row.label}</dt>
              <dd className={cn('tnum text-right text-body-xs', valueTones[row.tone ?? 'neutral'])}>
                {row.value}
              </dd>
            </Fragment>
          ))}
        </dl>
      ) : null}
      {extra === undefined ? null : <div className="mt-2">{extra}</div>}
      {note === undefined ? null : (
        <p className="mt-1 text-body-xs text-wrap text-muted-foreground-subtle">{note}</p>
      )}
      {hint === undefined ? null : <p className="mt-2 text-body-xs text-link">{hint}</p>}
    </div>
  )
}

interface TooltipProps extends TooltipPanelProps {
  /**
   * The mark being described. Return exactly ONE element — it is the box the
   * panel measures itself against and the node `aria-describedby` is put on.
   */
  children: ReactNode
  /**
   * `start` (default) lines the panel's left edge up with the mark's, as the hover-states frame
   * draws it over a matrix cell. `center` centres it under the mark, as the mock's term card sits.
   * `end` (sz:pi) lines the panel's right edge up with the mark's, for a mark at a row's far end
   * (Public Issues' ⓘ, the mock's `pi-info` card).
   */
  align?: 'start' | 'center' | 'end' | undefined
  /**
   * The side it opens on when it fits. Default `below`; `above` for the mock's chip tip; `left`
   * for a vertical rail at the right edge, falling back to `below` when the left has no room.
   */
  side?: TooltipSide | undefined
  /**
   * How long a mouse rests on the mark before the panel opens, in ms. Default 0. A term takes the
   * mock's 250, so a pointer sweeping across a row of labels does not flash a card per label.
   * Focus and a tap open at once.
   */
  delay?: number | undefined
  /**
   * A second way in, owned by the caller: the panel shows while this is true as well as on its
   * own hover, focus or tap. TermTip passes it when its term sits inside a button or a link,
   * which takes the focus the term itself cannot.
   */
  open?: boolean | undefined
  /** Escape or a press elsewhere put the panel away: the caller clears its `open` here. */
  onDismiss?: (() => void) | undefined
  /** The panel's id, when the caller points a node other than the mark at it. */
  panelId?: string | undefined
}

/**
 * What the hover-states frame draws over a sector cell, a holdings tile, a
 * collapsed sidebar row or an info button: a titled panel of labelled figures
 * that appears under the mark. A chart that draws its own marks shares one
 * `ChartPopover` across them instead.
 *
 * Three ways in, one per kind of reader: a mouse hovers, the keyboard
 * focuses, a finger taps. The frame says "Phones have no hover, so these rows
 * do not apply to mobile", and this used to ignore touch on that word; the
 * 2026-09-28 design refresh reverses it, because a figure only a mouse can
 * reach is a fact the page states to some readers and not others. A tap
 * toggles the panel and a tap anywhere else puts it away. Mouse and finger
 * are told apart by `pointerType`, not a `(hover: hover)` media query, which
 * would misread a laptop with a touchscreen.
 *
 * Portalled and `fixed`, for the same reason Popover is: these marks sit
 * inside cards and scrollers that clip their overflow, and once the panel is
 * out of the flow its coordinates are viewport coordinates, so clamping is
 * arithmetic rather than a library.
 *
 * Inside a modal `<dialog>` the portal target changes, as Popover's does: the
 * dialog is in the top layer, so a panel portalled to `<body>` paints under
 * it — invisible, yet still open and still claiming Escape. The panel goes
 * into the dialog instead and is promoted to the top layer as a
 * `popover="manual"`, whose containing block is the viewport again.
 *
 * The panel itself can be hovered (WCAG 1.4.13): a mouse that leaves the mark
 * has `CLOSE_DELAY_MS` to reach it, and the panel stays while the pointer is
 * over it, so a member can move onto the figures to read or select them.
 *
 * It arrives the way the reference's do: a fade and a 4px slide away from the
 * mark, over the med step, and leaves on a fade over the fast one. Arriving
 * needs the side it landed on before the panel is first styled — a
 * `@starting-style` is spent on the first style the browser computes, and
 * measuring the panel computes one — so each open measures an invisible copy
 * first and only then mounts the panel that animates, already in place.
 * Leaving keeps it mounted for the fade, inert and hidden from assistive
 * technology: to a screen reader it is gone the moment it closes.
 *
 * The clamping below is deliberately NOT Popover's. A tooltip has no focus to
 * trap or hand back and no `aria-haspopup` — sharing Popover would mean
 * switching both off. The placement
 * arithmetic is the only overlap, and lifting it into a shared hook is worth
 * doing once this feature's screens have settled; doing it mid-wave would put
 * every open menu in the app behind one refactor.
 */
export default function Tooltip({
  title,
  body,
  rows,
  hint,
  note,
  titleSize,
  variant = 'popover',
  align = 'start',
  side: preferredSide = 'below',
  delay = 0,
  open: openedByCaller = false,
  onDismiss,
  panelId: givenPanelId,
  className,
  children,
}: TooltipProps) {
  const ownPanelId = useId()
  const panelId = givenPanelId ?? ownPanelId
  const anchorRef = useRef<HTMLSpanElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [ownOpen, setOpen] = useState(false)
  const open = ownOpen || openedByCaller
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [placement, setPlacement] = useState<Placement | null>(null)

  // Mounted while open and through the exit fade after. Every open starts
  // unplaced, so it measures before it animates — see the component note.
  const [present, setPresent] = useState(false)
  const [wasOpen, setWasOpen] = useState(false)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setPresent(true)
      setPlacement(null)
    }
  }

  useEffect(() => {
    if (open || !present) return
    const timer = setTimeout(
      () => {
        setPresent(false)
      },
      motionDuration('--motion-fast', 120),
    )
    return () => {
      clearTimeout(timer)
    }
  }, [open, present])

  /**
   * The dialog the mark sits in, found once when the anchor mounts — see
   * Popover. Only used where the Popover API can lift the panel into the top
   * layer: without it a `fixed` panel inside the dialog would be placed
   * against the dialog's `translate`, not the viewport, so it stays on
   * `<body>` as before.
   */
  const [dialogHost, setDialogHost] = useState<HTMLDialogElement | null>(null)
  const bindAnchor = useCallback((node: HTMLSpanElement | null) => {
    anchorRef.current = node
    setDialogHost(node?.closest('dialog') ?? null)
  }, [])
  const host =
    dialogHost?.open === true && typeof dialogHost.showPopover === 'function' ? dialogHost : null

  /**
   * Into the top layer as it mounts, so the placement is measured in the
   * containing block the panel is painted in. A ref rather than the layout
   * effect, because the measured copy and the panel that animates are two
   * elements, and both have to be lifted.
   */
  const bindPanel = useCallback((node: HTMLDivElement | null) => {
    panelRef.current = node
    if (node?.popover === 'manual' && !node.matches(':popover-open')) node.showPopover()
  }, [])

  /**
   * `Element`, not `HTMLElement`: a donut segment and a radar point are SVG
   * nodes. Everything used below — `getBoundingClientRect`, `setAttribute`,
   * `contains` — is on Element, so nothing is given up by widening it.
   */
  const anchorNode = useCallback(() => anchorRef.current?.firstElementChild ?? null, [])

  const place = useCallback(() => {
    const anchor = anchorNode()
    const panel = panelRef.current
    if (!anchor || !panel) return

    const rect = anchor.getBoundingClientRect()
    const height = panel.offsetHeight
    const width = panel.offsetWidth

    // A head band's term card (mock `pi-tip`) hangs from the band's foot, flush, with its right
    // edge 20px inside the label's column, and 16 clear of either viewport edge. Outside a table
    // head there is no band to hang from, and it falls through to a centred card below.
    const column = variant === 'band' ? anchor.closest('th, [role="columnheader"]') : null
    const band = column?.closest('thead, [data-table-head]') ?? null
    if (column !== null && band !== null) {
      const top = band.getBoundingClientRect().bottom
      const left = Math.max(
        BAND_EDGE,
        Math.min(
          column.getBoundingClientRect().right - BAND_INSET - width,
          window.innerWidth - BAND_EDGE - width,
        ),
      )
      setPlacement((previous) =>
        previous?.top === top && previous.left === left && previous.side === 'below'
          ? previous
          : { top, left, side: 'below' },
      )
      return
    }

    // Centred on the mark's middle and held inside the viewport's height.
    const leftEdge = rect.left - GAP - width
    if (preferredSide === 'left' && leftEdge >= EDGE) {
      const bottommost = Math.max(EDGE, window.innerHeight - EDGE - height)
      const top = Math.min(Math.max(rect.top + (rect.height - height) / 2, EDGE), bottommost)
      setPlacement((previous) =>
        previous?.top === top && previous.left === leftEdge && previous.side === 'left'
          ? previous
          : { top, left: leftEdge, side: 'left' },
      )
      return
    }

    // The preferred side; the other only when the preferred one would run off
    // AND the other fits. A panel with room in neither place stays on its
    // preferred side and is clamped, which still leaves the mark visible.
    const below = rect.bottom + GAP
    const above = rect.top - GAP - height
    const belowFits = below + height <= window.innerHeight - EDGE
    const aboveFits = above >= EDGE
    const side =
      preferredSide !== 'above'
        ? !belowFits && aboveFits
          ? 'above'
          : 'below'
        : !aboveFits && belowFits
          ? 'below'
          : 'above'
    const top = side === 'above' ? above : below

    // By default the left edges line up with the mark's, which is what every
    // row of the hover frame draws — centring would push the panel off a cell
    // at the left margin of a wide matrix. A term centres, then clamps.
    const rightmost = Math.max(EDGE, window.innerWidth - EDGE - width)
    const anchorLeft =
      align === 'center' || variant === 'band'
        ? rect.left + (rect.width - width) / 2
        : align === 'end'
          ? rect.right - width
          : rect.left
    const left = Math.min(Math.max(anchorLeft, EDGE), rightmost)

    setPlacement((previous) =>
      previous?.top === top && previous.left === left && previous.side === side
        ? previous
        : { top, left, side },
    )
  }, [anchorNode, align, preferredSide, variant])

  useLayoutEffect(() => {
    if (!open) return

    // Before the browser paints, so the unplaced copy is never seen.
    place()
    // Capture phase because `scroll` does not bubble — a mark inside a table's
    // own scroller would otherwise drag its panel loose.
    window.addEventListener('scroll', place, { capture: true, passive: true })
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, { capture: true })
      window.removeEventListener('resize', place)
    }
  }, [open, host, place])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // Claimed, in capture, so it puts the panel away without also closing
      // the dialog or sheet under it: dismissing hover or focus content must
      // not move focus (WCAG 1.4.13), and closing a dialog does. A second
      // Escape reaches the dialog. `preventDefault` is what useModalDialog
      // checks, and it cancels the close request a modal `<dialog>` answers
      // natively — the same claim Popover makes.
      event.preventDefault()
      setOpen(false)
      onDismiss?.()
    }
    // A tap elsewhere puts a tapped-open panel away. A mouse closed it already
    // by leaving the mark, and a press on the mark itself is the tap's own.
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target
      if (target instanceof Node && anchorRef.current?.contains(target) === true) return
      if (target instanceof Node && panelRef.current?.contains(target) === true) return
      setOpen(false)
      onDismiss?.()
    }
    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [open, onDismiss])

  /**
   * `aria-describedby` is written onto the anchor rather than threaded through
   * a render prop, so a caller can wrap an `<svg>` path or a `<td>` it does not
   * control the props of. Popover already locates its anchor in the DOM for
   * the same reason; this writes one attribute to it and puts back exactly
   * what was there before.
   */
  useEffect(() => {
    if (!open) return
    const anchor = anchorNode()
    if (!anchor) return

    const previous = anchor.getAttribute('aria-describedby')
    anchor.setAttribute('aria-describedby', panelId)
    return () => {
      if (previous === null) anchor.removeAttribute('aria-describedby')
      else anchor.setAttribute('aria-describedby', previous)
    }
  }, [open, panelId, anchorNode])

  /**
   * A tap focuses the button it lands on, and focus opens this panel — but
   * the tap has already toggled it on `pointerup`, and a focus arriving
   * after that toggle (Chrome sends it with the tap's compatibility mouse
   * events) would reopen a panel the tap just closed. So the press notes that
   * it was a finger, and the focus it causes reads the note and stands aside.
   *
   * Cleared by the focus, the click or the cancel that ends the press, so it
   * only ever silences the focus the touch itself caused: a member who taps a
   * chart and then reaches for the keyboard gets the panel on the next Tab.
   */
  const touched = useRef(false)

  const cancelClose = () => {
    if (closeTimer.current === null) return
    clearTimeout(closeTimer.current)
    closeTimer.current = null
  }

  const cancelOpen = () => {
    if (openTimer.current === null) return
    clearTimeout(openTimer.current)
    openTimer.current = null
  }

  const openSoon = () => {
    if (delay <= 0) {
      setOpen(true)
      return
    }
    if (openTimer.current !== null) return
    openTimer.current = setTimeout(() => {
      openTimer.current = null
      setOpen(true)
    }, delay)
  }

  const closeSoon = () => {
    cancelClose()
    cancelOpen()
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null
      setOpen(false)
    }, CLOSE_DELAY_MS)
  }

  useEffect(() => {
    const timers = [closeTimer, openTimer]
    return () => {
      for (const timer of timers) if (timer.current !== null) clearTimeout(timer.current)
    }
  }, [])

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    touched.current = event.pointerType === 'touch'
  }

  const onPointerUp = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === 'touch') setOpen((current) => !current)
  }

  const onPointerOver = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === 'touch') return
    cancelClose()
    // Already open (a focus, the caller): no wait to sit through.
    if (ownOpen) return
    openSoon()
  }

  /**
   * `pointerover`/`pointerout` rather than enter/leave because they bubble,
   * and the wrapper below draws no box of its own to receive the non-bubbling
   * pair. The cost is that crossing between two children of the same mark
   * fires an `out`, so a move that stays inside the anchor is ignored —
   * without this the panel flickers once per inner span.
   *
   * A finger is ignored here as it is on the way in: a lifting finger fires
   * `pointerout` straight after `pointerup`, and would shut the panel the tap
   * has just opened.
   */
  const onPointerOut = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === 'touch') return
    const next = event.relatedTarget
    if (next instanceof Node && anchorRef.current?.contains(next) === true) return
    closeSoon()
  }

  const forgetTouch = () => {
    touched.current = false
  }

  return (
    <>
      {/* `contents` draws no box, so wrapping a table cell or a flex child
          changes no layout, exactly as Popover's anchor does. */}
      <span
        ref={bindAnchor}
        className="contents"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={forgetTouch}
        onClick={forgetTouch}
        onPointerOver={onPointerOver}
        onPointerOut={onPointerOut}
        onFocus={() => {
          const fromTouch = touched.current
          touched.current = false
          if (!fromTouch) setOpen(true)
        }}
        onBlur={() => {
          cancelOpen()
          setOpen(false)
        }}
      >
        {children}
      </span>
      {present
        ? createPortal(
            <div
              // A new element once placed, so its entrance starts from the
              // side it landed on; see the component note.
              key={placement === null ? 'measure' : 'placed'}
              ref={bindPanel}
              id={panelId}
              // Top layer inside a modal dialog; see the component note.
              popover={host !== null ? 'manual' : undefined}
              aria-hidden={open ? undefined : true}
              style={{
                top: placement?.top ?? 0,
                left: placement?.left ?? 0,
                // The measured copy: laid out, never seen.
                visibility: placement ? undefined : 'hidden',
              }}
              // Hoverable, see the component note. A finger is left alone
              // here as on the mark: its tap toggles, and elsewhere closes.
              onPointerEnter={(event) => {
                if (event.pointerType !== 'touch') cancelClose()
              }}
              onPointerLeave={(event) => {
                if (event.pointerType !== 'touch') closeSoon()
              }}
              // `inset-auto m-0 overflow-visible border-0 bg-transparent p-0`
              // undo the UA's `[popover]` box, its centring and Canvas fill;
              // inert outside a dialog.
              className={cn(
                'fixed inset-auto z-50 m-0 overflow-visible border-0 bg-transparent p-0',
                placement !== null &&
                  'transition-[opacity,translate] duration-med ease-out starting:opacity-0',
                // A term card only fades, as the mock's does; the others slide in from the mark.
                variant !== 'term' &&
                  (placement?.side === 'above'
                    ? 'starting:translate-y-1'
                    : placement?.side === 'left'
                      ? 'starting:translate-x-1'
                      : 'starting:-translate-y-1'),
                variant === 'term' && placement !== null && 'duration-(--motion-tip)',
                // The band card's `pi-tip-in`: a fade and a 4px drop over the fast step.
                variant === 'band' && placement !== null && 'duration-fast',
                !open && 'pointer-events-none opacity-0 duration-fast',
              )}
            >
              <TooltipPanel
                title={title}
                body={body}
                rows={rows}
                hint={hint}
                note={note}
                titleSize={titleSize}
                variant={variant}
                fade={false}
                className={className}
              />
            </div>,
            host ?? document.body,
          )
        : null}
    </>
  )
}
